import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import styled, { css } from 'styled-components';
import {
  FinancialInstitution,
  FinancialInstitutionBranch,
  searchPublicFinancialInstitutionBranches,
  searchPublicFinancialInstitutions,
} from '~/javascripts/admin/actions/financial-institutions';
import {
  createOrganizationApplication,
  CreateOrganizationApplicationParams,
  getPublicCorporateInfo,
  OrganizationApplication,
  ProposedShop,
  PublicPrivateMoney,
  PublicUserTagGroup,
} from '~/javascripts/admin/actions/organization-applications';
import {
  buildItemSelectOptions,
  filterItemOption,
  formatItemOptionLabel,
  ItemOption,
  ItemOptionGroup,
} from '~/javascripts/admin/components/organisms/tagSelectOptions';
import { USAGE_TYPE_OPTIONS, UsageType } from '~/javascripts/admin/constants/usageType';
import useMediaQuery from '~/javascripts/admin/hooks/useMediaQuery';
import {
  joinAddress,
  normalizeAddress,
  normalizeBuildingName,
} from '~/javascripts/admin/utils/characters/normalizeAddress';
import { toJapaneseBankingSystemHalfWidth } from '~/javascripts/admin/utils/characters/toHalfWidth';
import toKatakana from '~/javascripts/admin/utils/characters/toKatakana';
import { validateEmail } from '~/javascripts/admin/utils/email';
import PlusIcon from '../../../icons/plus.svg';
import Button from '../../atoms/Buttons';
import CheckBox from '../../atoms/CheckBox';
import DateTimeRaw from '../../atoms/DateTime';
import FA from '../../atoms/FontAwesome';
import Radio from '../../atoms/Radio';
import Select from '../../atoms/Select';
import SuggestTextBox from '../../atoms/SuggestTextBox';
import TextBox from '../../atoms/TextBox';

type UserTagGroupOption = {
  user_tag_group_id: string;
  field_option: 'required' | 'optional';
  // Present on real public-template responses; absent in preview mode where only
  // the form values are propagated via URL params.
  user_tag_group?: PublicUserTagGroup;
};

export type MerchantApplicationFormData = {
  templateId: string;
  title: string;
  applicationTermUrl: string;
  // 申込完了後にリダイレクトする先のURL（任意）。null なら完了画面のままリダイレクトしない。
  redirectUrl: string | null;
  // 加盟対象マネー（id/名称）。申込完了画面で表示する。
  privateMoneys: PublicPrivateMoney[];
  privateMoneyIds: string[];
  capitalFieldOption: 'required' | 'optional' | 'hidden';
  numberOfEmployeesFieldOption: 'required' | 'optional' | 'hidden';
  referralSourceFieldOption: 'required' | 'optional' | 'hidden';
  // 応募者向けフォーム説明文。空文字なら既定の案内文をフォールバック表示する。
  description: string;
  userTagGroupOptions?: UserTagGroupOption[];
};

type Props = {
  mode: 'preview' | 'form';
  data: MerchantApplicationFormData;
};

type FormValues = {
  business_type: 'individual' | 'corporate';
  // Individual fields
  trade_name: string;
  trade_name_furigana: string;
  // Corporate fields
  corporate_number: string;
  corporate_name: string;
  corporate_name_furigana: string;
  capital: string;
  number_of_employees: string;
  // Common fields
  representative_name: string;
  representative_furigana: string;
  representative_birth_date: string;
  postal_code: string;
  address: string;
  // 建物名・部屋番号。送信時に address へ連結するため API 上の項目ではない。
  building_name: string;
  tel: string;
  email: string;
  // 紹介元（個人・法人共通）
  referral_source: string;
  // Bank info
  bank_name: string;
  bank_code: string;
  bank_branch_name: string;
  bank_branch_code: string;
  bank_account_type: 'saving' | 'current';
  bank_account: string;
  bank_account_holder_name: string;
  // Shops
  shops: Array<{
    shop_name: string;
    shop_name_furigana: string;
    postal_code: string;
    address: string;
    building_name: string;
    tel: string;
    email: string;
    contact_person_name: string;
    contact_person_name_furigana: string;
    // 店舗形態（店舗ごと・フロント必須）。空文字は未選択。
    usage_type: UsageType | '';
    user_tag_ids: Record<string, string[]>;
  }>;
  // Agreement
  agreed: boolean;
};

// テンプレートの説明文（description）が未設定のときにタイトル下へ表示する既定の案内文。
const DEFAULT_FORM_DESCRIPTION =
  '個人事業主様・法人のお客様のお申し込みフォームです。該当する箇所のご記入をお願いします。';

// 生年月日カレンダーを未選択時に開いたときの初期表示月。多くの応募者は数十年前の
// 生まれのため、現在日基準よりも平均的に近いこの日付を初期表示にする。
const BIRTH_DATE_DEFAULT_VIEW = '1990-01-01';

// タッチデバイスでは react-datetime のカレンダー(月送りのみ)で数十年前の生年月日を
// 選ぶのが非常に手間なため、OS ネイティブの日付ピッカーに切り替える。
const COARSE_POINTER_QUERY = '(pointer: coarse)';
const BIRTH_DATE_MIN = '1900-01-01';
const toDateInputValue = (date: Date): string =>
  [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');

// 住所は応募者ごとの表記揺れが大きいため、入力例と補足文で書式を揃えるよう促す。
// blur 時の normalizeAddress とあわせてベストエフォートで正規化する。
const ADDRESS_PLACEHOLDER = '例）東京都渋谷区渋谷1-2-3';
const ADDRESS_HINT = '丁目・番地・号は「1-2-3」のように半角数字とハイフンで入力してください';
const BUILDING_NAME_PLACEHOLDER = '例）渋谷ビル5F';
const BUILDING_NAME_HINT = 'マンション名・ビル名・部屋番号がある場合は、こちらに入力してください';

// 紹介元の入力補助候補。自由入力も許容しつつ表記揺れを抑えるための代表値。
const REFERRAL_SOURCE_SUGGESTIONS: { key: string; label: string }[] = [
  { key: '自治体・行政', label: '自治体・行政' },
  { key: '運営事務局・サポートデスク', label: '運営事務局・サポートデスク' },
  { key: '商店街連合会・商店街振興組合', label: '商店街連合会・商店街振興組合' },
  { key: '加盟店向け説明会', label: '加盟店向け説明会' },
  { key: '地域金融機関', label: '地域金融機関' },
  { key: '広報物', label: '広報物' },
  { key: '加盟済の他店舗', label: '加盟済の他店舗' },
];

const DEFAULT_SHOP = {
  shop_name: '',
  shop_name_furigana: '',
  postal_code: '',
  address: '',
  building_name: '',
  tel: '',
  email: '',
  contact_person_name: '',
  contact_person_name_furigana: '',
  usage_type: '' as UsageType | '',
  user_tag_ids: {} as Record<string, string[]>,
};

// この画面は公開フォームで、申込者の多くはスマートフォンから開く。管理画面共通の
// atom(TextBox/Buttons/Radio)は他画面と共有しているため、タップ領域の拡大はここで
// ローカルに上書きし、管理画面全体には影響させない。
const Wrapper = styled.div`
  padding: 24px;
  max-width: 800px;
  margin: 0 auto;

  input:not([type='radio']):not([type='checkbox']),
  button {
    min-height: 44px;
  }

  input[type='radio'],
  input[type='checkbox'] {
    width: 20px;
    height: 20px;
  }

  @media (max-width: 480px) {
    padding: 16px 12px;
  }
`;

const DateTime = styled(DateTimeRaw)`
  margin-bottom: 0;
`;

const Action = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;

  ${Button} {
    min-width: 300px;
  }

  @media (max-width: 480px) {
    ${Button} {
      min-width: 0;
      width: 100%;
    }
  }
`;

const ShopAdditionAction = styled(Action)`
  flex-direction: column;
  align-items: center;
  margin-top: 8px;
  gap: 8px;
`;

const AddButton = styled(Button)`
  display: flex;
  gap: 8px;
  justify-content: center;
`;

const ShopAdditionText = styled.p`
  ${({ theme }) => css`
    ${theme.typography.body2.regular}
    text-align: center;
    color: ${theme.semanticColors.text.primary};
    margin: 0;
  `}
`;

const Preview = styled.div`
  ${({ theme }) => css`
    width: 100%;
    padding: 12px;
    ${theme.typography.body1}
    color: ${theme.colors.red[300]};
    border: 1px solid ${theme.colors.red[300]};
    border-radius: ${theme.borderRadius[4]};
    text-align: center;
    margin: 60px 0 40px;
  `}
`;

const Title = styled.h1`
  ${({ theme }) => css`
    ${theme.typography.heading2.bold}
    text-align: center;
    margin-bottom: 16px;
  `}
`;

const SubTitle = styled.h4`
  ${({ theme }) => css`
    ${theme.typography.body1.regular}
    text-align: left;
    margin-bottom: 40px;
    /* テンプレートの説明文は改行を含みうるため、改行を保持して表示する。 */
    white-space: pre-wrap;
    /* スキームなしURLや長いメールアドレス等、空白を含まない長い文字列がカードから
       はみ出さないよう、任意の位置で折り返す。 */
    overflow-wrap: anywhere;
    /* 説明文が少しだけ目立つよう、薄い背景色を敷いたカード状にする。 */
    background: ${theme.colors.gray[50]};
    padding: 16px;
    border-radius: ${theme.borderRadius[8]};
  `}
`;

const DescriptionLink = styled.a`
  ${({ theme }) => css`
    color: ${theme.semanticColors.text.link} !important;
    text-decoration: underline !important;
    word-break: break-all;
    &:hover {
      color: ${theme.semanticColors.text.linkHover} !important;
    }
  `}
`;

// 説明文(自由記述)中の http(s) URL を <a> に変換する。dangerouslySetInnerHTML は
// 使わず、URL 境界で文字列を分割して React 要素として組み立てる（非URL部分は React が
// 自動エスケープするため XSS 耐性がある）。改行は SubTitle の pre-wrap で保持される。
const URL_TOKEN_REGEX = /(https?:\/\/[^\s]+)/g;
const renderDescriptionWithLinks = (text: string): React.ReactNode =>
  text.split(URL_TOKEN_REGEX).map((part, index) =>
    /^https?:\/\//.test(part) ? (
      <DescriptionLink key={index} href={part} target="_blank" rel="noopener noreferrer">
        {part}
      </DescriptionLink>
    ) : (
      part
    )
  );

const Section = styled.div`
  ${({ theme }) => css`
    margin-bottom: 24px;
    padding: 24px;
    border: 1px solid ${theme.colors.gray[100]};
    border-radius: ${theme.borderRadius[8]};
    display: flex;
    flex-direction: column;
    gap: 16px;
    position: relative;

    @media (max-width: 480px) {
      padding: 16px;
    }
  `}
`;

const SectionTitle = styled.h2`
  ${({ theme }) => css`
    ${theme.typography.heading3.bold}
    margin: 0;
  `}
`;

const SectionSubTitle = styled.h4`
  ${({ theme }) => css`
    ${theme.typography.body1.bold}
    color: ${theme.semanticColors.text.secondary};
    margin: 0;
  `}
`;

const Field = styled.div.attrs({ 'data-field': true })`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FieldLabel = styled.div`
  ${({ theme }) => css`
    ${theme.typography.body2.regular}
    display: flex;
    gap: 8px;
    align-items: center;
  `}
`;

const FieldValue = styled.div``;

const FieldSubText = styled.p`
  ${({ theme }) => css`
    ${theme.typography.body3.regular}
    margin: 2px 0;
    color: ${theme.semanticColors.text.secondary};
  `}
`;

const FieldError = styled.p.attrs({ 'data-field-error': true })`
  ${({ theme }) => css`
    ${theme.typography.body3.regular}
    margin: 2px 0;
    color: ${theme.semanticColors.accent.red};
  `}
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 24px;
  margin-top: 8px;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  /* ラベル全体をタップ領域にして、小さなラジオボタンを狙わずに済むようにする。 */
  min-height: 44px;
  margin: 0;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${({ theme }) => theme.colors.gray[100]};
  margin: 0;
`;

const RequiredWrapper = styled.div`
  ${({ theme }) => css`
    ${theme.typography.body3.regular}
    color: ${theme.semanticColors.accent.red};
    padding: 4px 8px;
    border: 1px solid ${theme.semanticColors.accent.red};
    border-radius: 4px;
  `}
`;

const AgreementSection = styled.div``;

const AgreementTitle = styled.h4`
  ${({ theme }) => css`
    ${theme.typography.heading3.bold}
    padding-bottom: 12px;
    margin-bottom: 12px;
    border-bottom: 1px solid ${theme.colors.gray[100]};
  `}
`;

const Agreement = styled.h5`
  ${({ theme }) => css`
    ${theme.typography.body2.regular}
  `}
`;

const SuccessMessage = styled.div`
  ${({ theme }) => css`
    padding: 24px;
    background: ${theme.colors.green[50]};
    border: 1px solid ${theme.colors.green[300]};
    border-radius: ${theme.borderRadius[8]};
    text-align: center;
    margin: 40px 0;
    ${theme.typography.body1.regular}
  `}
`;

const CompletionDetails = styled.dl`
  ${({ theme }) => css`
    margin: 24px auto 0;
    max-width: 480px;
    text-align: left;
    display: grid;
    grid-template-columns: max-content 1fr;
    column-gap: 16px;
    row-gap: 8px;
    ${theme.typography.body2.regular}

    dt {
      color: ${theme.colors.gray[600]};
      white-space: nowrap;
    }
    dd {
      margin: 0;
      word-break: break-all;
    }
  `}
`;

const RedirectNotice = styled.div`
  ${({ theme }) => css`
    margin-top: 24px;
    padding-top: 16px;
    border-top: 1px solid ${theme.colors.green[300]};

    a {
      color: ${theme.colors.blue[500]};
      text-decoration: underline;
    }
  `}
`;

// 申込完了後リダイレクトのカウントダウン秒数（固定）。
const REDIRECT_COUNTDOWN_SECONDS = 10;

type CompletionScreenProps = {
  title: string;
  application: OrganizationApplication | null;
  privateMoneys: PublicPrivateMoney[];
  redirectUrl: string | null;
};

// 申込完了画面。申込フォーム名・申込番号・確認メール送信先・加盟対象マネーを表示し、
// テンプレートに redirectUrl が設定されていれば 10 秒カウントダウン後に自動遷移する。
const CompletionScreen = ({
  title,
  application,
  privateMoneys,
  redirectUrl,
}: CompletionScreenProps) => {
  const [remaining, setRemaining] = useState(REDIRECT_COUNTDOWN_SECONDS);

  useEffect(() => {
    if (redirectUrl === null) return undefined;
    if (remaining <= 0) {
      window.location.href = redirectUrl;
      return undefined;
    }
    const timer = window.setTimeout(() => setRemaining((prev) => prev - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [redirectUrl, remaining]);

  return (
    <Wrapper>
      <SuccessMessage>
        <h2>お申込みありがとうございます</h2>
        <p>申込みを受け付けました。確認メールをお送りしましたのでご確認ください。</p>
        <CompletionDetails>
          <dt>申込フォーム</dt>
          <dd>{title || '加盟店お申込みフォーム'}</dd>
          {application && (
            <>
              <dt>申込番号</dt>
              <dd>{application.id}</dd>
              <dt>確認メール送信先</dt>
              <dd>{application.email}</dd>
            </>
          )}
          {privateMoneys.length > 0 && (
            <>
              <dt>加盟対象マネー</dt>
              <dd>{privateMoneys.map((money) => money.name).join('、')}</dd>
            </>
          )}
        </CompletionDetails>
        {redirectUrl !== null && (
          <RedirectNotice>
            <p>{Math.max(remaining, 0)}秒後に自動的に移動します。</p>
            <a href={redirectUrl}>今すぐ移動する</a>
          </RedirectNotice>
        )}
      </SuccessMessage>
    </Wrapper>
  );
};

const ErrorMessage = styled.div`
  ${({ theme }) => css`
    padding: 24px;
    background: ${theme.colors.red[50]};
    border: 1px solid ${theme.colors.red[300]};
    border-radius: ${theme.borderRadius[8]};
    text-align: center;
    margin: 40px 0;
    ${theme.typography.body1.regular}
    color: ${theme.colors.red[500]};
  `}
`;

const TrashButton = styled(FA)<{ disabled?: boolean }>`
  position: absolute;
  top: 24px;
  right: 16px;
  cursor: pointer;
  color: #999;
  width: 32px;
  height: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 20px;
`;

const Required = () => <RequiredWrapper>必須</RequiredWrapper>;

/**
 * 生年月日の入力欄。個人事業主・法人の両方から使うため、片方だけ直して
 * ずれることのないようコンポーネントに切り出している。
 */
const BirthDateField = ({
  value,
  onChange,
  useNativePicker,
  maxDate,
}: {
  value: string;
  onChange: (value: string) => void;
  useNativePicker: boolean;
  maxDate: string;
}) =>
  useNativePicker ? (
    <TextBox
      fullWidth
      type="date"
      syncValue={value}
      onChange={(v) => onChange(v.target.value)}
      min={BIRTH_DATE_MIN}
      max={maxDate}
      autoComplete="bday"
    />
  ) : (
    <DateTime
      showIcon
      timeFormat={false}
      dateFormat="YYYY-MM-DD"
      viewDate={BIRTH_DATE_DEFAULT_VIEW}
      inputProps={{ placeholder: '日付を選択してください' }}
      value={value}
      onChange={(val) => val && onChange(val.format('YYYY-MM-DD'))}
    />
  );

const LookupRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 4px;

  ${Button} {
    min-width: 140px;
    white-space: nowrap;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;

    ${Button} {
      width: 100%;
    }
  }
`;

const LookupHint = styled.p`
  ${({ theme }) => css`
    ${theme.typography.body3.regular}
    margin: 0;
    color: ${theme.semanticColors.text.secondary};
  `}
`;

// Helper function to filter input to digits only
const filterDigitsOnly = (value: string): string => value.replace(/\D/g, '');

// Autofill section for a shop. Without it the browser treats every shop's
// address/tel/email as the same field group and fills them all with the
// applicant's own details.
const shopAutoCompleteSection = (index: number): string => `section-shop-${index}`;

// Financial-institution lookup: cap the number of suggestions shown, since
// short partial-match queries (e.g. 「銀行」) can hit many institutions.
const SUGGESTION_LIMIT = 20;
const SUGGESTION_DEBOUNCE_MS = 300;

type UserTagGroupWithItems = {
  id: string;
  name: string;
  fieldOption: 'required' | 'optional';
  maxSelections: number;
  // subgroup 見出しを反映した item Select オプション（useMemo で前計算）。
  hasSubgroups: boolean;
  itemOptions: ItemOption[] | ItemOptionGroup[];
  // value 照合用のフラット options（grouped 時は options が見出し構造で .value を持たないため）。
  flatItemOptions: ItemOption[];
};

const MerchantApplicationForm = ({ mode, data }: Props) => {
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>(
    'idle'
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // 送信APIのレスポンス。完了画面で申込番号(id)・確認メール送信先(email)の表示に使う。
  const [submittedApplication, setSubmittedApplication] = useState<OrganizationApplication | null>(
    null
  );
  const [corporateLookupStatus, setCorporateLookupStatus] = useState<'idle' | 'loading' | 'error'>(
    'idle'
  );
  const [corporateLookupError, setCorporateLookupError] = useState<string | null>(null);

  const {
    templateId,
    title,
    privateMoneyIds,
    privateMoneys,
    redirectUrl,
    capitalFieldOption,
    numberOfEmployeesFieldOption,
    referralSourceFieldOption,
    description,
    applicationTermUrl,
    userTagGroupOptions,
  } = data;

  const userTagGroups = useMemo<UserTagGroupWithItems[]>(() => {
    if (!userTagGroupOptions) return [];
    return userTagGroupOptions
      .map((option) => {
        const detail = option.user_tag_group;
        if (!detail) return null;
        // Backend already filters inactive items, but keep the guard for
        // defense-in-depth and to preserve prior behavior.
        const activeItems = detail.items.filter((item) => item.is_active);
        if (activeItems.length === 0) return null;
        // subgroups は active・作成順（サーバ側で保証）。無ければ空 → フラット表示。
        const subgroups = (detail.subgroups ?? []).map((sg) => ({
          id: sg.id,
          name: sg.name_ja || sg.name_en,
        }));
        // grouped options を前計算（group は不変なので毎レンダー再計算を避ける）。
        // item を共通の {id,name,subgroupId,subgroupName} へ正規化してから渡す。
        const subgroupNameById = new Map(subgroups.map((sg) => [sg.id, sg.name]));
        const normalizedItems = activeItems.map((item) => ({
          id: item.id,
          name: item.name_ja || item.name_en,
          subgroupId: item.user_tag_subgroup_id,
          subgroupName:
            item.user_tag_subgroup_id !== null
              ? subgroupNameById.get(item.user_tag_subgroup_id) ?? null
              : null,
        }));
        const { options: itemOptions, flat: flatItemOptions } = buildItemSelectOptions(
          normalizedItems,
          subgroups
        );
        return {
          id: detail.id,
          name: detail.name_ja || detail.name_en,
          fieldOption: option.field_option,
          maxSelections: detail.max_selections,
          hasSubgroups: subgroups.length > 0,
          itemOptions,
          flatItemOptions,
        };
      })
      .filter((g): g is UserTagGroupWithItems => g !== null);
  }, [userTagGroupOptions]);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    trigger,
    clearErrors,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      business_type: 'individual',
      trade_name: '',
      trade_name_furigana: '',
      corporate_number: '',
      corporate_name: '',
      corporate_name_furigana: '',
      capital: '',
      number_of_employees: '',
      representative_name: '',
      representative_furigana: '',
      representative_birth_date: '',
      postal_code: '',
      address: '',
      building_name: '',
      tel: '',
      email: '',
      referral_source: '',
      bank_name: '',
      bank_code: '',
      bank_branch_name: '',
      bank_branch_code: '',
      bank_account_type: 'saving',
      bank_account: '',
      bank_account_holder_name: '',
      shops: [{ ...DEFAULT_SHOP }],
      agreed: false,
    },
  });

  const {
    fields: shopFields,
    append: appendShop,
    remove: removeShop,
  } = useFieldArray({
    control,
    name: 'shops',
  });

  // 生年月日はタッチデバイスでのみ OS ネイティブの日付ピッカーを使う。
  const useNativeDatePicker = useMediaQuery(COARSE_POINTER_QUERY);
  const today = useMemo(() => toDateInputValue(new Date()), []);

  const businessType = watch('business_type');
  const agreed = watch('agreed');
  const corporateNumber = watch('corporate_number');
  const isCorporateNumberComplete = /^\d{13}$/.test(corporateNumber);

  const handleAddShop = () => {
    appendShop({ ...DEFAULT_SHOP });
  };

  // --- Financial-institution autofill -------------------------------------
  // Entering a 4-digit institution code auto-fills the institution name (and
  // vice versa via name suggestions); once the institution is known, the same
  // works for branch code/name. This reduces typos and notation
  // inconsistencies in the bank fields.
  const [institutionSuggestions, setInstitutionSuggestions] = useState<FinancialInstitution[]>([]);
  const [branchSuggestions, setBranchSuggestions] = useState<FinancialInstitutionBranch[]>([]);
  const [bankLookupError, setBankLookupError] = useState<string | null>(null);
  const [branchLookupError, setBranchLookupError] = useState<string | null>(null);
  // On-blur verification: set when the entered name has no exact match in the
  // financial institution master.
  const [bankNameError, setBankNameError] = useState<string | null>(null);
  const [branchNameError, setBranchNameError] = useState<string | null>(null);
  // Institution confirmed via code lookup or suggestion selection. Its UUID is
  // required for branch search, so manual name entry alone does not set it.
  const institutionRef = useRef<FinancialInstitution | null>(null);
  // Institution the current branch fields were auto-filled from, used to clear
  // stale branch values when the user switches to a different institution.
  const branchInstitutionIdRef = useRef<string | null>(null);
  // Sequence counters to drop out-of-order async responses.
  const institutionSearchSeq = useRef(0);
  const branchSearchSeq = useRef(0);
  const corporateLookupSeq = useRef(0);
  const institutionSuggestTimer = useRef<number | undefined>(undefined);
  const branchSuggestTimer = useRef<number | undefined>(undefined);

  // Clear pending debounce timers on unmount and bump the sequences so that
  // in-flight responses hit the stale guard instead of calling setState on an
  // unmounted component.
  useEffect(
    () => () => {
      window.clearTimeout(institutionSuggestTimer.current);
      window.clearTimeout(branchSuggestTimer.current);
      institutionSearchSeq.current += 1;
      branchSearchSeq.current += 1;
    },
    []
  );

  const clearBranchFieldsIfStale = (institution: FinancialInstitution) => {
    if (branchInstitutionIdRef.current && branchInstitutionIdRef.current !== institution.id) {
      setValue('bank_branch_name', '');
      setValue('bank_branch_code', '');
      branchInstitutionIdRef.current = null;
    }
    setBranchSuggestions([]);
    setBranchLookupError(null);
    setBranchNameError(null);
  };

  const confirmInstitution = (institution: FinancialInstitution) => {
    institutionRef.current = institution;
    setBankNameError(null);
    clearBranchFieldsIfStale(institution);
  };

  // Resolve the institution needed for branch search: prefer the confirmed
  // one, otherwise try to look it up from the entered 4-digit code. Only the
  // ref is updated here (no setState) so callers stay in control of state
  // updates behind their own stale-sequence guards.
  const resolveInstitution = async (): Promise<FinancialInstitution | null> => {
    if (institutionRef.current) return institutionRef.current;
    const code = getValues('bank_code');
    if (!/^\d{4}$/.test(code)) return null;
    try {
      const institutions = await searchPublicFinancialInstitutions({ code });
      if (institutions.length === 1) {
        institutionRef.current = institutions[0];
        return institutions[0];
      }
    } catch {
      // Fall through: branch autofill is best-effort.
    }
    return null;
  };

  const handleBankNameInput = (value: string) => {
    // Manual edits invalidate the previously confirmed institution and any
    // in-flight lookup (suggestion fetch or on-blur verification).
    institutionSearchSeq.current += 1;
    institutionRef.current = null;
    setBankLookupError(null);
    setBankNameError(null);
    window.clearTimeout(institutionSuggestTimer.current);
    const query = value.trim();
    if (mode === 'preview' || query === '') {
      setInstitutionSuggestions([]);
      return;
    }
    institutionSuggestTimer.current = window.setTimeout(async () => {
      const seq = ++institutionSearchSeq.current;
      try {
        const institutions = await searchPublicFinancialInstitutions({ name: query });
        if (seq !== institutionSearchSeq.current) return;
        setInstitutionSuggestions(institutions.slice(0, SUGGESTION_LIMIT));
      } catch {
        if (seq !== institutionSearchSeq.current) return;
        setInstitutionSuggestions([]);
      }
    }, SUGGESTION_DEBOUNCE_MS);
  };

  const handleInstitutionSelect = (key: string) => {
    const institution = institutionSuggestions.find((i) => i.id === key);
    if (!institution) return;
    // Same reasoning as handleBranchSelect: supersede an in-flight on-blur
    // verification so it cannot re-raise bankNameError after the selection.
    institutionSearchSeq.current += 1;
    setValue('bank_name', institution.name, { shouldValidate: true });
    setValue('bank_code', institution.code, { shouldValidate: true });
    confirmInstitution(institution);
    setInstitutionSuggestions([]);
    setBankLookupError(null);
  };

  const handleBankCodeInput = async (digits: string) => {
    const seq = ++institutionSearchSeq.current;
    institutionRef.current = null;
    setBankLookupError(null);
    clearErrors('bank_code');
    if (mode === 'preview' || !/^\d{4}$/.test(digits)) return;
    try {
      const institutions = await searchPublicFinancialInstitutions({ code: digits });
      if (seq !== institutionSearchSeq.current) return;
      if (institutions.length === 1) {
        setValue('bank_name', institutions[0].name, { shouldValidate: true });
        confirmInstitution(institutions[0]);
      } else if (institutions.length === 0) {
        setBankLookupError(
          '該当する金融機関が見つかりませんでした。金融機関コードをご確認ください。'
        );
      }
    } catch {
      if (seq !== institutionSearchSeq.current) return;
      setBankLookupError('金融機関情報の取得に失敗しました。時間をおいて再度お試しください。');
    }
  };

  const handleBranchNameInput = (value: string) => {
    branchSearchSeq.current += 1;
    branchInstitutionIdRef.current = null;
    setBranchLookupError(null);
    setBranchNameError(null);
    window.clearTimeout(branchSuggestTimer.current);
    const query = value.trim();
    if (mode === 'preview' || query === '') {
      setBranchSuggestions([]);
      return;
    }
    branchSuggestTimer.current = window.setTimeout(async () => {
      const seq = ++branchSearchSeq.current;
      const institution = await resolveInstitution();
      if (seq !== branchSearchSeq.current) return;
      if (!institution) {
        setBranchSuggestions([]);
        return;
      }
      try {
        const branches = await searchPublicFinancialInstitutionBranches(institution.id, {
          branch_name: query,
        });
        if (seq !== branchSearchSeq.current) return;
        setBranchSuggestions(branches.slice(0, SUGGESTION_LIMIT));
      } catch {
        if (seq !== branchSearchSeq.current) return;
        setBranchSuggestions([]);
      }
    }, SUGGESTION_DEBOUNCE_MS);
  };

  const handleBranchSelect = (key: string) => {
    const branch = branchSuggestions.find((b) => b.id === key);
    if (!branch) return;
    // A selection supersedes the on-blur verification that may still be in
    // flight for the text the user had typed before re-opening the list.
    // Without this the late response would re-raise branchNameError against
    // the old text even though the field now holds a master-resolved value.
    branchSearchSeq.current += 1;
    setValue('bank_branch_name', branch.branch_name, { shouldValidate: true });
    setValue('bank_branch_code', branch.branch_code, { shouldValidate: true });
    branchInstitutionIdRef.current = branch.bankfill.id;
    setBranchSuggestions([]);
    setBranchLookupError(null);
    setBranchNameError(null);
  };

  const handleBranchCodeInput = async (digits: string) => {
    const seq = ++branchSearchSeq.current;
    branchInstitutionIdRef.current = null;
    setBranchLookupError(null);
    clearErrors('bank_branch_code');
    if (mode === 'preview' || !/^\d{3}$/.test(digits)) return;
    const institution = await resolveInstitution();
    if (seq !== branchSearchSeq.current) return;
    // Without a confirmed institution the branch cannot be identified
    // uniquely; leave the field as manual input.
    if (!institution) return;
    try {
      const branches = await searchPublicFinancialInstitutionBranches(institution.id, {
        branch_code: digits,
      });
      if (seq !== branchSearchSeq.current) return;
      if (branches.length === 1) {
        setValue('bank_branch_name', branches[0].branch_name, { shouldValidate: true });
        branchInstitutionIdRef.current = institution.id;
        // The name now comes from the master, so a previous on-blur
        // verification failure no longer applies.
        setBranchNameError(null);
      } else if (branches.length === 0) {
        setBranchLookupError('該当する支店が見つかりませんでした。支店コードをご確認ください。');
      }
    } catch {
      if (seq !== branchSearchSeq.current) return;
      setBranchLookupError('支店情報の取得に失敗しました。時間をおいて再度お試しください。');
    }
  };

  // Show the digit-count error as soon as the code field loses focus with a
  // non-empty incomplete value, instead of waiting for submit. Empty fields
  // are left to the submit-time required check.
  const handleBankCodeBlur = () => {
    if (getValues('bank_code') !== '') trigger('bank_code');
  };

  const handleBranchCodeBlur = () => {
    if (getValues('bank_branch_code') !== '') trigger('bank_branch_code');
  };

  // Verify a manually entered institution name on blur: if it does not
  // exactly match an official name (e.g. 「みずほ銀」), warn the user. When it
  // does match, treat it like a suggestion selection and fill the code.
  const handleBankNameBlur = () => {
    window.clearTimeout(institutionSuggestTimer.current);
    const name = getValues('bank_name').trim();
    if (mode === 'preview' || name === '') return;
    if (institutionRef.current && institutionRef.current.name === name) return;
    const seq = ++institutionSearchSeq.current;
    (async () => {
      try {
        const institutions = await searchPublicFinancialInstitutions({ name });
        if (seq !== institutionSearchSeq.current) return;
        const exact = institutions.find((institution) => institution.name === name);
        if (exact) {
          setValue('bank_code', exact.code, { shouldValidate: true });
          confirmInstitution(exact);
        } else {
          setBankNameError(
            '正式名称と一致しません。名称の一部を入力して候補から選択してください。'
          );
        }
      } catch {
        // Verification is best-effort; do not block manual entry on API failure.
      }
    })();
  };

  const handleBranchNameBlur = () => {
    window.clearTimeout(branchSuggestTimer.current);
    const name = getValues('bank_branch_name').trim();
    if (mode === 'preview' || name === '') return;
    // Already auto-filled from the master and unchanged since.
    if (branchInstitutionIdRef.current) return;
    const seq = ++branchSearchSeq.current;
    (async () => {
      // Without a confirmed institution the branch cannot be verified.
      const institution = await resolveInstitution();
      if (seq !== branchSearchSeq.current || !institution) return;
      try {
        const branches = await searchPublicFinancialInstitutionBranches(institution.id, {
          branch_name: name,
        });
        if (seq !== branchSearchSeq.current) return;
        const exact = branches.find((branch) => branch.branch_name === name);
        if (exact) {
          setValue('bank_branch_code', exact.branch_code, { shouldValidate: true });
          branchInstitutionIdRef.current = institution.id;
          setBranchNameError(null);
        } else {
          setBranchNameError(
            '正式名称と一致しません。名称の一部を入力して候補から選択してください。'
          );
        }
      } catch {
        // Verification is best-effort; do not block manual entry on API failure.
      }
    })();
  };
  // -------------------------------------------------------------------------

  // Editing the corporate number invalidates the previous lookup result. The
  // message is rendered with data-field-error, so leaving it in place would
  // let it hijack the submit-time scroll target (see #7431) even though the
  // number has since been corrected. Bumping the sequence also abandons a
  // lookup still in flight, so its response can neither autofill the corporate
  // fields nor raise an error against the number the user has moved on from.
  const handleCorporateNumberInput = () => {
    corporateLookupSeq.current += 1;
    setCorporateLookupStatus('idle');
    setCorporateLookupError(null);
  };

  // Look up corporate info from the National Tax Agency by the entered
  // corporate number and auto-fill the related fields. This reduces manual
  // entry mistakes and notation inconsistencies.
  const handleCorporateLookup = async () => {
    if (mode === 'preview' || !isCorporateNumberComplete) return;

    const seq = ++corporateLookupSeq.current;
    setCorporateLookupStatus('loading');
    setCorporateLookupError(null);

    try {
      const info = await getPublicCorporateInfo(corporateNumber);
      if (seq !== corporateLookupSeq.current) return;
      setValue('corporate_name', info.name, { shouldValidate: true });
      setValue('corporate_name_furigana', info.furigana, { shouldValidate: true });
      setValue('postal_code', info.postal_code, { shouldValidate: true });
      // The registry returns the address up to the house number, so it goes in
      // the street field; the building name stays for the applicant to fill in.
      setValue('address', normalizeAddress(info.address), { shouldValidate: true });
      setCorporateLookupStatus('idle');
    } catch (e: unknown) {
      // throwOnError throws the parsed JSON body (a Promise) for 4xx responses
      // and a string for 5xx. Resolve it to distinguish "not found" from other
      // failures so we can show an actionable message.
      const body = await Promise.resolve(e).catch(() => null);
      if (seq !== corporateLookupSeq.current) return;
      const type =
        body && typeof body === 'object' && 'type' in body
          ? (body as { type?: string }).type
          : undefined;
      setCorporateLookupStatus('error');
      setCorporateLookupError(
        type === 'corporate_number_not_found'
          ? '該当する法人が見つかりませんでした。法人番号をご確認ください。'
          : '法人情報の取得に失敗しました。時間をおいて再度お試しください。'
      );
    }
  };

  // Scroll to the first error field when validation fails
  const onInvalid = useCallback(() => {
    // Wait for error messages to render, then scroll to the first one
    setTimeout(() => {
      const firstErrorElement = document.querySelector('[data-field-error]');
      if (firstErrorElement) {
        firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Try to focus the input in the same field
        const fieldContainer = firstErrorElement.closest('[data-field]');
        const input = fieldContainer?.querySelector('input, select, textarea');
        // Skip focusing comboboxes (SuggestTextBox): a programmatic focus opens
        // the suggestion dropdown, which would cover the error message below the
        // input. The field is still scrolled into view.
        if (input instanceof HTMLElement && input.getAttribute('role') !== 'combobox') {
          input.focus();
        }
      }
    }, 100);
  }, []);

  const onSubmit = async (values: FormValues) => {
    if (mode === 'preview') return;

    setSubmitStatus('submitting');
    setErrorMessage(null);

    try {
      const getUserTagGroupItemIds = (shop: FormValues['shops'][number]) => {
        const tagIds = Object.values(shop.user_tag_ids || {})
          .flat()
          .filter(Boolean);
        return tagIds.length > 0 ? tagIds : undefined;
      };

      const headOfficeShop: ProposedShop = {
        shop_name: values.shops[0].shop_name,
        shop_name_furigana: values.shops[0].shop_name_furigana,
        postal_code: values.shops[0].postal_code,
        address: joinAddress(values.shops[0].address, values.shops[0].building_name),
        tel: values.shops[0].tel,
        email: values.shops[0].email,
        contact_person_name: values.shops[0].contact_person_name,
        contact_person_name_furigana: values.shops[0].contact_person_name_furigana,
        usage_type: values.shops[0].usage_type || undefined,
        user_tag_group_item_ids: getUserTagGroupItemIds(values.shops[0]),
      };

      const additionalShops: ProposedShop[] = values.shops.slice(1).map((shop) => ({
        shop_name: shop.shop_name,
        shop_name_furigana: shop.shop_name_furigana,
        postal_code: shop.postal_code,
        address: joinAddress(shop.address, shop.building_name),
        tel: shop.tel,
        email: shop.email,
        contact_person_name: shop.contact_person_name,
        contact_person_name_furigana: shop.contact_person_name_furigana,
        usage_type: shop.usage_type || undefined,
        user_tag_group_item_ids: getUserTagGroupItemIds(shop),
      }));

      const params: CreateOrganizationApplicationParams = {
        template_id: templateId,
        business_type: values.business_type,
        name: values.business_type === 'individual' ? values.trade_name : values.corporate_name,
        furigana:
          values.business_type === 'individual'
            ? values.trade_name_furigana
            : values.corporate_name_furigana,
        representative_name: values.representative_name,
        representative_furigana: values.representative_furigana,
        representative_birth_date: values.representative_birth_date,
        postal_code: values.postal_code,
        address: joinAddress(values.address, values.building_name),
        tel: values.tel,
        email: values.email,
        referral_source: values.referral_source,
        bank_name: values.bank_name,
        bank_code: values.bank_code,
        bank_branch_name: values.bank_branch_name,
        bank_branch_code: values.bank_branch_code,
        bank_account_type: values.bank_account_type,
        bank_account: values.bank_account,
        bank_account_holder_name: values.bank_account_holder_name,
        private_money_ids: privateMoneyIds,
        head_office_shop: headOfficeShop,
        additional_shops: additionalShops.length > 0 ? additionalShops : undefined,
      };

      // Add conditional fields (corporate only; usage_type is now per-shop)
      if (values.business_type === 'corporate') {
        params.corporate_number = values.corporate_number;
        if (values.capital) {
          params.capital = parseInt(values.capital, 10);
        }
        if (values.number_of_employees) {
          params.number_of_employees = parseInt(values.number_of_employees, 10);
        }
      }

      const application = await createOrganizationApplication(params);
      setSubmittedApplication(application);
      setSubmitStatus('success');
    } catch (e: any) {
      setSubmitStatus('error');
      setErrorMessage(e?.message ?? '申込みの送信中にエラーが発生しました');
    }
  };

  if (submitStatus === 'success') {
    return (
      <CompletionScreen
        title={title}
        application={submittedApplication}
        privateMoneys={privateMoneys}
        redirectUrl={redirectUrl}
      />
    );
  }

  return (
    <Wrapper>
      {mode === 'preview' && <Preview>プレビュー</Preview>}

      <Title>{title || '加盟店お申込みフォーム'}</Title>
      <SubTitle>{renderDescriptionWithLinks(description || DEFAULT_FORM_DESCRIPTION)}</SubTitle>

      {submitStatus === 'error' && errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}

      <form onSubmit={handleSubmit(onSubmit, onInvalid)}>
        <Section>
          <SectionTitle>基本情報</SectionTitle>
          <Field>
            <FieldLabel>
              事業体の種類 <Required />
            </FieldLabel>
            <FieldValue>
              <Controller
                name="business_type"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <RadioGroup>
                    <RadioLabel>
                      <Radio
                        name="business_type"
                        checked={value === 'individual'}
                        setChecked={() => onChange('individual')}
                      />
                      個人事業主
                    </RadioLabel>
                    <RadioLabel>
                      <Radio
                        name="business_type"
                        checked={value === 'corporate'}
                        setChecked={() => onChange('corporate')}
                      />
                      法人
                    </RadioLabel>
                  </RadioGroup>
                )}
              />
            </FieldValue>
          </Field>
          <Divider />

          {businessType === 'individual' && (
            <>
              <Field>
                <FieldLabel>
                  屋号（ない場合は氏名） <Required />
                </FieldLabel>
                <FieldValue>
                  <Controller
                    name="trade_name"
                    control={control}
                    rules={{ required: '必須項目です' }}
                    render={({ field: { onChange, value } }) => (
                      <TextBox fullWidth syncValue={value} onChange={onChange} />
                    )}
                  />
                  {errors.trade_name && <FieldError>{errors.trade_name.message}</FieldError>}
                </FieldValue>
              </Field>
              <Field>
                <FieldLabel>
                  屋号（フリガナ） <Required />
                </FieldLabel>
                <FieldValue>
                  <Controller
                    name="trade_name_furigana"
                    control={control}
                    rules={{ required: '必須項目です' }}
                    render={({ field: { onChange, value } }) => (
                      <TextBox
                        fullWidth
                        syncValue={value}
                        onChange={onChange}
                        onBlur={() => onChange(toKatakana(value))}
                      />
                    )}
                  />
                  {errors.trade_name_furigana && (
                    <FieldError>{errors.trade_name_furigana.message}</FieldError>
                  )}
                </FieldValue>
              </Field>
              <Field>
                <FieldLabel>
                  郵便番号 <Required />
                </FieldLabel>
                <FieldValue>
                  <Controller
                    name="postal_code"
                    control={control}
                    rules={{
                      required: '必須項目です',
                      pattern: {
                        value: /^\d{7}$/,
                        message: '7桁の数字を入力してください',
                      },
                    }}
                    render={({ field: { onChange, value } }) => (
                      <TextBox
                        fullWidth
                        syncValue={value}
                        onChange={(v) => onChange(filterDigitsOnly(v.target.value))}
                        placeholder="例）0001111"
                        maxLength={7}
                        inputMode="numeric"
                        autoComplete="postal-code"
                      />
                    )}
                  />
                  {errors.postal_code && <FieldError>{errors.postal_code.message}</FieldError>}
                </FieldValue>
              </Field>
              <Field>
                <FieldLabel>
                  納税地住所（開業届の記載住所） <Required />
                </FieldLabel>
                <FieldValue>
                  <Controller
                    name="address"
                    control={control}
                    rules={{ required: '必須項目です' }}
                    render={({ field: { onChange, value } }) => (
                      <TextBox
                        fullWidth
                        syncValue={value}
                        onChange={onChange}
                        onBlur={() => onChange(normalizeAddress(value))}
                        placeholder={ADDRESS_PLACEHOLDER}
                        autoComplete="street-address"
                      />
                    )}
                  />
                  <FieldSubText>{ADDRESS_HINT}</FieldSubText>
                  {errors.address && <FieldError>{errors.address.message}</FieldError>}
                </FieldValue>
              </Field>
              <Field>
                <FieldLabel>建物名・部屋番号</FieldLabel>
                <FieldValue>
                  <Controller
                    name="building_name"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <TextBox
                        fullWidth
                        syncValue={value}
                        onChange={onChange}
                        onBlur={() => onChange(normalizeBuildingName(value))}
                        placeholder={BUILDING_NAME_PLACEHOLDER}
                      />
                    )}
                  />
                  <FieldSubText>{BUILDING_NAME_HINT}</FieldSubText>
                </FieldValue>
              </Field>
              <Field>
                <FieldLabel>
                  代表者氏名 <Required />
                </FieldLabel>
                <FieldValue>
                  <Controller
                    name="representative_name"
                    control={control}
                    rules={{ required: '必須項目です' }}
                    render={({ field: { onChange, value } }) => (
                      <TextBox
                        fullWidth
                        syncValue={value}
                        onChange={onChange}
                        placeholder="例）山田　太郎"
                        autoComplete="name"
                      />
                    )}
                  />
                  <FieldSubText>姓と名の間にスペースを入力してください</FieldSubText>
                  {errors.representative_name && (
                    <FieldError>{errors.representative_name.message}</FieldError>
                  )}
                </FieldValue>
              </Field>
              <Field>
                <FieldLabel>
                  代表者氏名（フリガナ） <Required />
                </FieldLabel>
                <FieldValue>
                  <Controller
                    name="representative_furigana"
                    control={control}
                    rules={{ required: '必須項目です' }}
                    render={({ field: { onChange, value } }) => (
                      <TextBox
                        fullWidth
                        syncValue={value}
                        onChange={onChange}
                        onBlur={() => onChange(toKatakana(value))}
                        placeholder="例）ヤマダ　タロウ"
                      />
                    )}
                  />
                  <FieldSubText>姓と名の間にスペースを入力してください</FieldSubText>
                  {errors.representative_furigana && (
                    <FieldError>{errors.representative_furigana.message}</FieldError>
                  )}
                </FieldValue>
              </Field>
              <Field>
                <FieldLabel>
                  代表者生年月日 <Required />
                </FieldLabel>
                <FieldValue>
                  <Controller
                    name="representative_birth_date"
                    control={control}
                    rules={{ required: '必須項目です' }}
                    render={({ field: { onChange, value } }) => (
                      <BirthDateField
                        value={value}
                        onChange={onChange}
                        useNativePicker={useNativeDatePicker}
                        maxDate={today}
                      />
                    )}
                  />
                  {errors.representative_birth_date && (
                    <FieldError>{errors.representative_birth_date.message}</FieldError>
                  )}
                </FieldValue>
              </Field>
              <Field>
                <FieldLabel>
                  電話番号 <Required />
                </FieldLabel>
                <FieldValue>
                  <Controller
                    name="tel"
                    control={control}
                    rules={{
                      required: '必須項目です',
                      pattern: {
                        value: /^\d{10,11}$/,
                        message: '10桁または11桁の数字を入力してください',
                      },
                    }}
                    render={({ field: { onChange, value } }) => (
                      <TextBox
                        fullWidth
                        syncValue={value}
                        onChange={(v) => onChange(filterDigitsOnly(v.target.value))}
                        placeholder="例）08012345678"
                        maxLength={11}
                        type="tel"
                        autoComplete="tel"
                      />
                    )}
                  />
                  {errors.tel && <FieldError>{errors.tel.message}</FieldError>}
                </FieldValue>
              </Field>
              <Field>
                <FieldLabel>
                  メールアドレス <Required />
                </FieldLabel>
                <FieldValue>
                  <Controller
                    name="email"
                    control={control}
                    rules={{
                      required: '必須項目です',
                      validate: (value) => validateEmail(value),
                    }}
                    render={({ field: { onChange, value } }) => (
                      <TextBox
                        fullWidth
                        syncValue={value}
                        onChange={onChange}
                        placeholder="例）pokepay@example.com"
                        type="email"
                        inputMode="email"
                        autoComplete="email"
                      />
                    )}
                  />
                  <FieldSubText>
                    承認後、本メールアドレスは加盟事業者管理画面のログインIDとして利用されます。お間違えのないようご入力ください
                  </FieldSubText>
                  {errors.email && <FieldError>{errors.email.message}</FieldError>}
                </FieldValue>
              </Field>
            </>
          )}

          {businessType === 'corporate' && (
            <>
              <Field>
                <FieldLabel>
                  法人番号 <Required />
                </FieldLabel>
                <FieldValue>
                  <Controller
                    name="corporate_number"
                    control={control}
                    rules={{
                      required: '必須項目です',
                      pattern: {
                        value: /^\d{13}$/,
                        message: '13桁の数字を入力してください',
                      },
                    }}
                    render={({ field: { onChange, value } }) => (
                      <TextBox
                        fullWidth
                        syncValue={value}
                        onChange={(v) => {
                          onChange(filterDigitsOnly(v.target.value));
                          handleCorporateNumberInput();
                        }}
                        maxLength={13}
                        inputMode="numeric"
                      />
                    )}
                  />
                  {errors.corporate_number && (
                    <FieldError>{errors.corporate_number.message}</FieldError>
                  )}
                  <LookupRow>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={
                        mode === 'preview' ||
                        !isCorporateNumberComplete ||
                        corporateLookupStatus === 'loading'
                      }
                      onClick={handleCorporateLookup}
                    >
                      {corporateLookupStatus === 'loading' ? '取得中...' : '情報取得'}
                    </Button>
                    <LookupHint>
                      13桁の法人番号を入力すると、法人名・郵便番号・住所を自動入力できます
                    </LookupHint>
                  </LookupRow>
                  {corporateLookupStatus === 'error' && corporateLookupError && (
                    <FieldError>{corporateLookupError}</FieldError>
                  )}
                </FieldValue>
              </Field>
              <Field>
                <FieldLabel>
                  法人名 <Required />
                </FieldLabel>
                <FieldValue>
                  <Controller
                    name="corporate_name"
                    control={control}
                    rules={{ required: '必須項目です' }}
                    render={({ field: { onChange, value } }) => (
                      <TextBox fullWidth syncValue={value} onChange={onChange} />
                    )}
                  />
                  {errors.corporate_name && (
                    <FieldError>{errors.corporate_name.message}</FieldError>
                  )}
                </FieldValue>
              </Field>
              <Field>
                <FieldLabel>
                  法人名（フリガナ） <Required />
                </FieldLabel>
                <FieldValue>
                  <Controller
                    name="corporate_name_furigana"
                    control={control}
                    rules={{ required: '必須項目です' }}
                    render={({ field: { onChange, value } }) => (
                      <TextBox
                        fullWidth
                        syncValue={value}
                        onChange={onChange}
                        onBlur={() => onChange(toKatakana(value))}
                      />
                    )}
                  />
                  {errors.corporate_name_furigana && (
                    <FieldError>{errors.corporate_name_furigana.message}</FieldError>
                  )}
                </FieldValue>
              </Field>
              <Field>
                <FieldLabel>
                  郵便番号 <Required />
                </FieldLabel>
                <FieldValue>
                  <Controller
                    name="postal_code"
                    control={control}
                    rules={{
                      required: '必須項目です',
                      pattern: {
                        value: /^\d{7}$/,
                        message: '7桁の数字を入力してください',
                      },
                    }}
                    render={({ field: { onChange, value } }) => (
                      <TextBox
                        fullWidth
                        syncValue={value}
                        onChange={(v) => onChange(filterDigitsOnly(v.target.value))}
                        placeholder="例）0001111"
                        maxLength={7}
                        inputMode="numeric"
                        autoComplete="postal-code"
                      />
                    )}
                  />
                  {errors.postal_code && <FieldError>{errors.postal_code.message}</FieldError>}
                </FieldValue>
              </Field>
              <Field>
                <FieldLabel>
                  本店所在地（登記住所） <Required />
                </FieldLabel>
                <FieldValue>
                  <Controller
                    name="address"
                    control={control}
                    rules={{ required: '必須項目です' }}
                    render={({ field: { onChange, value } }) => (
                      <TextBox
                        fullWidth
                        syncValue={value}
                        onChange={onChange}
                        onBlur={() => onChange(normalizeAddress(value))}
                        placeholder={ADDRESS_PLACEHOLDER}
                        autoComplete="street-address"
                      />
                    )}
                  />
                  <FieldSubText>{ADDRESS_HINT}</FieldSubText>
                  {errors.address && <FieldError>{errors.address.message}</FieldError>}
                </FieldValue>
              </Field>
              <Field>
                <FieldLabel>建物名・部屋番号</FieldLabel>
                <FieldValue>
                  <Controller
                    name="building_name"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <TextBox
                        fullWidth
                        syncValue={value}
                        onChange={onChange}
                        onBlur={() => onChange(normalizeBuildingName(value))}
                        placeholder={BUILDING_NAME_PLACEHOLDER}
                      />
                    )}
                  />
                  <FieldSubText>{BUILDING_NAME_HINT}</FieldSubText>
                </FieldValue>
              </Field>
              <Field>
                <FieldLabel>
                  代表者氏名 <Required />
                </FieldLabel>
                <FieldValue>
                  <Controller
                    name="representative_name"
                    control={control}
                    rules={{ required: '必須項目です' }}
                    render={({ field: { onChange, value } }) => (
                      <TextBox
                        fullWidth
                        syncValue={value}
                        onChange={onChange}
                        placeholder="例）山田　太郎"
                        autoComplete="name"
                      />
                    )}
                  />
                  <FieldSubText>姓と名の間にスペースを入力してください</FieldSubText>
                  {errors.representative_name && (
                    <FieldError>{errors.representative_name.message}</FieldError>
                  )}
                </FieldValue>
              </Field>
              <Field>
                <FieldLabel>
                  代表者氏名（フリガナ） <Required />
                </FieldLabel>
                <FieldValue>
                  <Controller
                    name="representative_furigana"
                    control={control}
                    rules={{ required: '必須項目です' }}
                    render={({ field: { onChange, value } }) => (
                      <TextBox
                        fullWidth
                        syncValue={value}
                        onChange={onChange}
                        onBlur={() => onChange(toKatakana(value))}
                        placeholder="例）ヤマダ　タロウ"
                      />
                    )}
                  />
                  <FieldSubText>姓と名の間にスペースを入力してください</FieldSubText>
                  {errors.representative_furigana && (
                    <FieldError>{errors.representative_furigana.message}</FieldError>
                  )}
                </FieldValue>
              </Field>
              <Field>
                <FieldLabel>
                  代表者生年月日 <Required />
                </FieldLabel>
                <FieldValue>
                  <Controller
                    name="representative_birth_date"
                    control={control}
                    rules={{ required: '必須項目です' }}
                    render={({ field: { onChange, value } }) => (
                      <BirthDateField
                        value={value}
                        onChange={onChange}
                        useNativePicker={useNativeDatePicker}
                        maxDate={today}
                      />
                    )}
                  />
                  {errors.representative_birth_date && (
                    <FieldError>{errors.representative_birth_date.message}</FieldError>
                  )}
                </FieldValue>
              </Field>
              <Field>
                <FieldLabel>
                  電話番号 <Required />
                </FieldLabel>
                <FieldValue>
                  <Controller
                    name="tel"
                    control={control}
                    rules={{
                      required: '必須項目です',
                      pattern: {
                        value: /^\d{10,11}$/,
                        message: '10桁または11桁の数字を入力してください',
                      },
                    }}
                    render={({ field: { onChange, value } }) => (
                      <TextBox
                        fullWidth
                        syncValue={value}
                        onChange={(v) => onChange(filterDigitsOnly(v.target.value))}
                        placeholder="例）08012345678"
                        maxLength={11}
                        type="tel"
                        autoComplete="tel"
                      />
                    )}
                  />
                  {errors.tel && <FieldError>{errors.tel.message}</FieldError>}
                </FieldValue>
              </Field>
              <Field>
                <FieldLabel>
                  メールアドレス <Required />
                </FieldLabel>
                <FieldValue>
                  <Controller
                    name="email"
                    control={control}
                    rules={{
                      required: '必須項目です',
                      validate: (value) => validateEmail(value),
                    }}
                    render={({ field: { onChange, value } }) => (
                      <TextBox
                        fullWidth
                        syncValue={value}
                        onChange={onChange}
                        placeholder="例）pokepay@example.com"
                        type="email"
                        inputMode="email"
                        autoComplete="email"
                      />
                    )}
                  />
                  <FieldSubText>
                    承認後、本メールアドレスは加盟事業者管理画面のログインIDとして利用されます。お間違えのないようご入力ください
                  </FieldSubText>
                  {errors.email && <FieldError>{errors.email.message}</FieldError>}
                </FieldValue>
              </Field>
              {capitalFieldOption !== 'hidden' && (
                <Field>
                  <FieldLabel>
                    資本金（円） {capitalFieldOption === 'required' && <Required />}
                  </FieldLabel>
                  <FieldValue>
                    <Controller
                      name="capital"
                      control={control}
                      rules={capitalFieldOption === 'required' ? { required: '必須項目です' } : {}}
                      render={({ field: { onChange, value } }) => (
                        <TextBox
                          fullWidth
                          syncValue={value}
                          onChange={(v) => onChange(filterDigitsOnly(v.target.value))}
                          inputMode="numeric"
                        />
                      )}
                    />
                    {errors.capital && <FieldError>{errors.capital.message}</FieldError>}
                  </FieldValue>
                </Field>
              )}
              {numberOfEmployeesFieldOption !== 'hidden' && (
                <Field>
                  <FieldLabel>
                    従業員数 {numberOfEmployeesFieldOption === 'required' && <Required />}
                  </FieldLabel>
                  <FieldValue>
                    <Controller
                      name="number_of_employees"
                      control={control}
                      rules={
                        numberOfEmployeesFieldOption === 'required'
                          ? { required: '必須項目です' }
                          : {}
                      }
                      render={({ field: { onChange, value } }) => (
                        <TextBox
                          fullWidth
                          syncValue={value}
                          onChange={(v) => onChange(filterDigitsOnly(v.target.value))}
                          inputMode="numeric"
                        />
                      )}
                    />
                    {errors.number_of_employees && (
                      <FieldError>{errors.number_of_employees.message}</FieldError>
                    )}
                  </FieldValue>
                </Field>
              )}
            </>
          )}
          {referralSourceFieldOption !== 'hidden' && (
            <Field>
              <FieldLabel>
                紹介元 {referralSourceFieldOption === 'required' && <Required />}
              </FieldLabel>
              <FieldValue>
                <Controller
                  name="referral_source"
                  control={control}
                  rules={
                    referralSourceFieldOption === 'required' ? { required: '必須項目です' } : {}
                  }
                  render={({ field: { onChange, value } }) => (
                    <SuggestTextBox
                      fullWidth
                      syncValue={value}
                      maxLength={256}
                      suggestions={REFERRAL_SOURCE_SUGGESTIONS.filter(
                        (s) => value === '' || s.label.includes(value)
                      )}
                      onChangeText={onChange}
                      onSelect={onChange}
                    />
                  )}
                />
                <FieldSubText>
                  候補から選択できます。該当がない場合は自由に入力してください。
                </FieldSubText>
                {errors.referral_source && (
                  <FieldError>{errors.referral_source.message}</FieldError>
                )}
              </FieldValue>
            </Field>
          )}
        </Section>

        <Section>
          <SectionTitle>振り込み先情報</SectionTitle>

          <Field>
            <FieldLabel>
              金融機関名 <Required />
            </FieldLabel>
            <FieldValue>
              <Controller
                name="bank_name"
                control={control}
                rules={{ required: '必須項目です' }}
                render={({ field: { onChange, value } }) => (
                  <SuggestTextBox
                    fullWidth
                    syncValue={value}
                    suggestions={institutionSuggestions.map((institution) => ({
                      key: institution.id,
                      label: institution.name,
                      note: institution.code,
                    }))}
                    onChangeText={(v) => {
                      onChange(v);
                      handleBankNameInput(v);
                    }}
                    onSelect={handleInstitutionSelect}
                    onBlur={handleBankNameBlur}
                  />
                )}
              />
              <FieldSubText>
                名称の一部を入力すると候補が表示され、選択すると金融機関コードも自動入力されます。直接入力する場合は〇〇銀行、〇〇信用金庫、〇〇信用組合まで入力してください
              </FieldSubText>
              {bankNameError && <FieldError>{bankNameError}</FieldError>}
              {errors.bank_name && <FieldError>{errors.bank_name.message}</FieldError>}
            </FieldValue>
          </Field>

          <Field>
            <FieldLabel>
              金融機関コード <Required />
            </FieldLabel>
            <FieldValue>
              <Controller
                name="bank_code"
                control={control}
                rules={{
                  required: '必須項目です',
                  pattern: {
                    value: /^\d{4}$/,
                    message: '4桁の数字を入力してください',
                  },
                }}
                render={({ field: { onChange, value } }) => (
                  <TextBox
                    fullWidth
                    syncValue={value}
                    onChange={(v) => {
                      const digits = filterDigitsOnly(v.target.value);
                      onChange(digits);
                      handleBankCodeInput(digits);
                    }}
                    onBlur={handleBankCodeBlur}
                    placeholder="例）0001"
                    maxLength={4}
                    inputMode="numeric"
                  />
                )}
              />
              <FieldSubText>4桁のコードを入力すると金融機関名が自動入力されます</FieldSubText>
              {bankLookupError && <FieldError>{bankLookupError}</FieldError>}
              {errors.bank_code && <FieldError>{errors.bank_code.message}</FieldError>}
            </FieldValue>
          </Field>

          <Field>
            <FieldLabel>
              支店名 <Required />
            </FieldLabel>
            <FieldValue>
              <Controller
                name="bank_branch_name"
                control={control}
                rules={{ required: '必須項目です' }}
                render={({ field: { onChange, value } }) => (
                  <SuggestTextBox
                    fullWidth
                    syncValue={value}
                    suggestions={branchSuggestions.map((branch) => ({
                      key: branch.id,
                      label: branch.branch_name,
                      note: branch.branch_code,
                    }))}
                    onChangeText={(v) => {
                      onChange(v);
                      handleBranchNameInput(v);
                    }}
                    onSelect={handleBranchSelect}
                    onBlur={handleBranchNameBlur}
                  />
                )}
              />
              <FieldSubText>
                金融機関の入力後、支店名の一部を入力すると候補が表示され、選択すると支店コードも自動入力されます。直接入力する場合は〇〇支店まで入力してください
              </FieldSubText>
              {branchNameError && <FieldError>{branchNameError}</FieldError>}
              {errors.bank_branch_name && (
                <FieldError>{errors.bank_branch_name.message}</FieldError>
              )}
            </FieldValue>
          </Field>

          <Field>
            <FieldLabel>
              支店コード <Required />
            </FieldLabel>
            <FieldValue>
              <Controller
                name="bank_branch_code"
                control={control}
                rules={{
                  required: '必須項目です',
                  pattern: {
                    value: /^\d{3}$/,
                    message: '3桁の数字を入力してください',
                  },
                }}
                render={({ field: { onChange, value } }) => (
                  <TextBox
                    fullWidth
                    syncValue={value}
                    onChange={(v) => {
                      const digits = filterDigitsOnly(v.target.value);
                      onChange(digits);
                      handleBranchCodeInput(digits);
                    }}
                    onBlur={handleBranchCodeBlur}
                    maxLength={3}
                    inputMode="numeric"
                  />
                )}
              />
              <FieldSubText>
                金融機関の入力後、3桁のコードを入力すると支店名が自動入力されます
              </FieldSubText>
              {branchLookupError && <FieldError>{branchLookupError}</FieldError>}
              {errors.bank_branch_code && (
                <FieldError>{errors.bank_branch_code.message}</FieldError>
              )}
            </FieldValue>
          </Field>

          <Field>
            <FieldLabel>
              口座種別 <Required />
            </FieldLabel>
            <FieldValue>
              <Controller
                name="bank_account_type"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <RadioGroup>
                    <RadioLabel>
                      <Radio
                        name="bank_account_type"
                        checked={value === 'saving'}
                        setChecked={() => onChange('saving')}
                      />
                      普通
                    </RadioLabel>
                    <RadioLabel>
                      <Radio
                        name="bank_account_type"
                        checked={value === 'current'}
                        setChecked={() => onChange('current')}
                      />
                      当座
                    </RadioLabel>
                  </RadioGroup>
                )}
              />
            </FieldValue>
          </Field>

          <Field>
            <FieldLabel>
              口座番号 <Required />
            </FieldLabel>
            <FieldValue>
              <Controller
                name="bank_account"
                control={control}
                rules={{ required: '必須項目です' }}
                render={({ field: { onChange, value } }) => (
                  <TextBox
                    fullWidth
                    syncValue={value}
                    onChange={(v) => onChange(filterDigitsOnly(v.target.value))}
                    placeholder="例）1234567"
                    maxLength={7}
                    inputMode="numeric"
                  />
                )}
              />
              {errors.bank_account && <FieldError>{errors.bank_account.message}</FieldError>}
            </FieldValue>
          </Field>

          <Field>
            <FieldLabel>
              口座名義（半角ｶﾀｶﾅ） <Required />
            </FieldLabel>
            <FieldValue>
              <Controller
                name="bank_account_holder_name"
                control={control}
                rules={{ required: '必須項目です' }}
                render={({ field: { onChange, value } }) => (
                  <TextBox
                    fullWidth
                    syncValue={value}
                    onChange={onChange}
                    onBlur={() => onChange(toJapaneseBankingSystemHalfWidth(toKatakana(value)))}
                    placeholder="例）ﾔﾏﾀﾞﾀﾛｳ "
                  />
                )}
              />
              {errors.bank_account_holder_name && (
                <FieldError>{errors.bank_account_holder_name.message}</FieldError>
              )}
            </FieldValue>
          </Field>
        </Section>

        {shopFields.map((field, index) => (
          <Section key={field.id}>
            <SectionTitle>店舗情報</SectionTitle>

            <SectionSubTitle>店舗 {index + 1}</SectionSubTitle>

            <Field>
              <FieldLabel>
                店舗名 <Required />
              </FieldLabel>
              <FieldValue>
                <Controller
                  name={`shops.${index}.shop_name`}
                  control={control}
                  rules={{ required: '必須項目です' }}
                  render={({ field: { onChange, value } }) => (
                    <TextBox fullWidth syncValue={value} onChange={onChange} placeholder="店舗名" />
                  )}
                />
                {errors.shops?.[index]?.shop_name && (
                  <FieldError>{errors.shops[index]?.shop_name?.message}</FieldError>
                )}
              </FieldValue>
            </Field>

            <Field>
              <FieldLabel>
                店舗名（フリガナ） <Required />
              </FieldLabel>
              <FieldValue>
                <Controller
                  name={`shops.${index}.shop_name_furigana`}
                  control={control}
                  rules={{ required: '必須項目です' }}
                  render={({ field: { onChange, value } }) => (
                    <TextBox
                      fullWidth
                      syncValue={value}
                      onChange={onChange}
                      onBlur={() => onChange(toKatakana(value))}
                      placeholder="店舗名（フリガナ）"
                    />
                  )}
                />
                {errors.shops?.[index]?.shop_name_furigana && (
                  <FieldError>{errors.shops[index]?.shop_name_furigana?.message}</FieldError>
                )}
              </FieldValue>
            </Field>

            <Field>
              <FieldLabel>
                店舗郵便番号 <Required />
              </FieldLabel>
              <FieldValue>
                <Controller
                  name={`shops.${index}.postal_code`}
                  control={control}
                  rules={{
                    required: '必須項目です',
                    pattern: {
                      value: /^\d{7}$/,
                      message: '7桁の数字を入力してください',
                    },
                  }}
                  render={({ field: { onChange, value } }) => (
                    <TextBox
                      fullWidth
                      syncValue={value}
                      onChange={(v) => onChange(filterDigitsOnly(v.target.value))}
                      placeholder="例）0001111"
                      maxLength={7}
                      inputMode="numeric"
                      autoComplete={`${shopAutoCompleteSection(index)} postal-code`}
                    />
                  )}
                />
                {errors.shops?.[index]?.postal_code && (
                  <FieldError>{errors.shops[index]?.postal_code?.message}</FieldError>
                )}
              </FieldValue>
            </Field>

            <Field>
              <FieldLabel>
                店舗住所 <Required />
              </FieldLabel>
              <FieldValue>
                <Controller
                  name={`shops.${index}.address`}
                  control={control}
                  rules={{ required: '必須項目です' }}
                  render={({ field: { onChange, value } }) => (
                    <TextBox
                      fullWidth
                      syncValue={value}
                      onChange={onChange}
                      onBlur={() => onChange(normalizeAddress(value))}
                      placeholder={ADDRESS_PLACEHOLDER}
                      autoComplete={`${shopAutoCompleteSection(index)} street-address`}
                    />
                  )}
                />
                <FieldSubText>{ADDRESS_HINT}</FieldSubText>
                {errors.shops?.[index]?.address && (
                  <FieldError>{errors.shops[index]?.address?.message}</FieldError>
                )}
              </FieldValue>
            </Field>

            <Field>
              <FieldLabel>店舗建物名・部屋番号</FieldLabel>
              <FieldValue>
                <Controller
                  name={`shops.${index}.building_name`}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <TextBox
                      fullWidth
                      syncValue={value}
                      onChange={onChange}
                      onBlur={() => onChange(normalizeBuildingName(value))}
                      placeholder={BUILDING_NAME_PLACEHOLDER}
                    />
                  )}
                />
                <FieldSubText>{BUILDING_NAME_HINT}</FieldSubText>
              </FieldValue>
            </Field>

            <Field>
              <FieldLabel>
                店舗電話番号 <Required />
              </FieldLabel>
              <FieldValue>
                <Controller
                  name={`shops.${index}.tel`}
                  control={control}
                  rules={{
                    required: '必須項目です',
                    pattern: {
                      value: /^\d{10,11}$/,
                      message: '10桁または11桁の数字を入力してください',
                    },
                  }}
                  render={({ field: { onChange, value } }) => (
                    <TextBox
                      fullWidth
                      syncValue={value}
                      onChange={(v) => onChange(filterDigitsOnly(v.target.value))}
                      placeholder="例）0312345678"
                      maxLength={11}
                      type="tel"
                      autoComplete={`${shopAutoCompleteSection(index)} tel`}
                    />
                  )}
                />
                {errors.shops?.[index]?.tel && (
                  <FieldError>{errors.shops[index]?.tel?.message}</FieldError>
                )}
              </FieldValue>
            </Field>

            <Field>
              <FieldLabel>
                店舗メールアドレス <Required />
              </FieldLabel>
              <FieldValue>
                <Controller
                  name={`shops.${index}.email`}
                  control={control}
                  rules={{
                    required: '必須項目です',
                    validate: (value) => validateEmail(value),
                  }}
                  render={({ field: { onChange, value } }) => (
                    <TextBox
                      fullWidth
                      syncValue={value}
                      onChange={onChange}
                      placeholder="例）pokepay@example.com"
                      type="email"
                      inputMode="email"
                      autoComplete={`${shopAutoCompleteSection(index)} email`}
                    />
                  )}
                />
                {errors.shops?.[index]?.email && (
                  <FieldError>{errors.shops[index]?.email?.message}</FieldError>
                )}
              </FieldValue>
            </Field>

            <Field>
              <FieldLabel>
                店舗担当者名 <Required />
              </FieldLabel>
              <FieldValue>
                <Controller
                  name={`shops.${index}.contact_person_name`}
                  control={control}
                  rules={{ required: '必須項目です' }}
                  render={({ field: { onChange, value } }) => (
                    <TextBox
                      fullWidth
                      syncValue={value}
                      onChange={onChange}
                      autoComplete={`${shopAutoCompleteSection(index)} name`}
                    />
                  )}
                />
                <FieldSubText>姓と名の間にスペースを入力してください</FieldSubText>
                {errors.shops?.[index]?.contact_person_name && (
                  <FieldError>{errors.shops[index]?.contact_person_name?.message}</FieldError>
                )}
              </FieldValue>
            </Field>

            <Field>
              <FieldLabel>
                店舗担当者名（フリガナ） <Required />
              </FieldLabel>
              <FieldValue>
                <Controller
                  name={`shops.${index}.contact_person_name_furigana`}
                  control={control}
                  rules={{ required: '必須項目です' }}
                  render={({ field: { onChange, value } }) => (
                    <TextBox
                      fullWidth
                      syncValue={value}
                      onChange={onChange}
                      onBlur={() => onChange(toKatakana(value))}
                    />
                  )}
                />
                <FieldSubText>姓と名の間にスペースを入力してください</FieldSubText>
                {errors.shops?.[index]?.contact_person_name_furigana && (
                  <FieldError>
                    {errors.shops[index]?.contact_person_name_furigana?.message}
                  </FieldError>
                )}
              </FieldValue>
            </Field>

            <Field>
              <FieldLabel>
                店舗形態 <Required />
              </FieldLabel>
              <FieldValue>
                <Controller
                  name={`shops.${index}.usage_type`}
                  control={control}
                  rules={{ required: '必須項目です' }}
                  render={({ field: { onChange, value } }) => (
                    <Select<{ value: UsageType; label: string }, false, never>
                      options={USAGE_TYPE_OPTIONS}
                      value={USAGE_TYPE_OPTIONS.find((opt) => opt.value === value) ?? null}
                      onChange={(opt) => onChange(opt ? opt.value : '')}
                      placeholder="店舗形態を選択してください"
                    />
                  )}
                />
                {errors.shops?.[index]?.usage_type && (
                  <FieldError>{errors.shops[index]?.usage_type?.message}</FieldError>
                )}
              </FieldValue>
            </Field>

            {userTagGroups.map((group) => (
              <Field key={group.id}>
                <FieldLabel>
                  {group.name} {group.fieldOption === 'required' && <Required />}
                </FieldLabel>
                <FieldValue>
                  <Controller
                    name={`shops.${index}.user_tag_ids.${group.id}`}
                    control={control}
                    rules={
                      group.fieldOption === 'required'
                        ? { validate: (v) => (v && v.length > 0) || '必須項目です' }
                        : {}
                    }
                    render={({ field: { onChange, value } }) => {
                      // options/flat は useMemo で前計算済み。value 照合には flat を使う
                      //（grouped 時は options が見出し構造で .value を持たないため）。
                      const { itemOptions, flatItemOptions, hasSubgroups } = group;
                      const isSingleSelect = group.maxSelections === 1;
                      return (
                        <Select
                          fullWidth
                          isMulti={!isSingleSelect}
                          options={itemOptions}
                          filterOption={hasSubgroups ? (filterItemOption as any) : undefined}
                          formatOptionLabel={
                            hasSubgroups ? (formatItemOptionLabel as any) : undefined
                          }
                          value={
                            isSingleSelect
                              ? flatItemOptions.find((opt) => (value || [])[0] === opt.value) ??
                                null
                              : flatItemOptions.filter((opt) => (value || []).includes(opt.value))
                          }
                          onChange={(opts) => {
                            if (isSingleSelect) {
                              onChange(opts ? [opts.value] : []);
                            } else {
                              onChange(opts?.map((o) => o.value) ?? []);
                            }
                          }}
                          placeholder="選択してください"
                          isClearable={group.fieldOption !== 'required'}
                        />
                      );
                    }}
                  />
                  {errors.shops?.[index]?.user_tag_ids?.[group.id] && (
                    <FieldError>
                      {(errors.shops[index]?.user_tag_ids as any)?.[group.id]?.message}
                    </FieldError>
                  )}
                </FieldValue>
              </Field>
            ))}

            {index === shopFields.length - 1 && (
              <ShopAdditionAction>
                <AddButton type="button" variant="outline" onClick={handleAddShop}>
                  <img src={PlusIcon} alt="plus icon" /> 続けて店舗情報を追加する
                </AddButton>
                <ShopAdditionText>複数店舗をお申し込みされる際にご利用ください</ShopAdditionText>
              </ShopAdditionAction>
            )}

            {shopFields.length > 1 && (
              <TrashButton icon="trash-alt" onClick={() => removeShop(index)} />
            )}
          </Section>
        ))}

        <AgreementSection data-agreement-section>
          <AgreementTitle>同意事項</AgreementTitle>
          <Agreement>
            <p>必ず加盟店規約をご確認の上お申し込みください</p>
            <Controller
              name="agreed"
              control={control}
              rules={{ required: '同意が必要です' }}
              render={({ field: { onChange, value } }) => (
                <Button
                  type="button"
                  variant="outline"
                  style={{ display: 'flex', gap: '8px' }}
                  onClick={() => {
                    if (applicationTermUrl) {
                      window.open(applicationTermUrl, '_blank', 'noopener,noreferrer');
                    }
                    onChange(true);
                  }}
                >
                  <CheckBox checked={value} onChange={onChange} noErrors />
                  加盟店規約に同意します
                </Button>
              )}
            />

            {errors.agreed && <FieldError>{errors.agreed.message}</FieldError>}
          </Agreement>
        </AgreementSection>

        <Action>
          <Button
            type="submit"
            disabled={mode === 'preview' || submitStatus === 'submitting' || !agreed}
          >
            {submitStatus === 'submitting' ? '送信中...' : '同意して申し込む'}
          </Button>
        </Action>
      </form>
    </Wrapper>
  );
};

export default MerchantApplicationForm;
